import { useState, useEffect, useMemo } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog'
import { Button } from '../ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select'
import type { LlmConfigChatModelOption } from '../lib/llmConfigModels'
import {
  getChatErrorToastMessage,
  SESSION_MODEL_RELOCK_DIALOG_BODY,
  SESSION_MODEL_RELOCK_TITLE,
} from '../lib/chatApiErrors'

type ChatModelRelockDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  apiBase: string
  /** Session to update; when null the dialog is inert. */
  chatId: string | null
  modelOptions: LlmConfigChatModelOption[]
  onRelocked: (chatId: string, modelId: string) => void
  onRequestError?: (message: string) => void
}

export function ChatModelRelockDialog({
  open,
  onOpenChange,
  apiBase,
  chatId,
  modelOptions,
  onRelocked,
  onRequestError,
}: ChatModelRelockDialogProps) {
  const [selectedId, setSelectedId] = useState<string>('')
  const [submitting, setSubmitting] = useState(false)

  const optionIds = useMemo(() => new Set(modelOptions.map(m => m.id)), [modelOptions])
  /** Radix Select throws if `value` is not one of the items — never use "". */
  const selectValue = useMemo(() => {
    if (modelOptions.length === 0) return ''
    if (selectedId && optionIds.has(selectedId)) return selectedId
    return modelOptions[0].id
  }, [modelOptions, optionIds, selectedId])

  useEffect(() => {
    if (!open || modelOptions.length === 0) return
    setSelectedId(prev => {
      if (prev && optionIds.has(prev)) return prev
      return modelOptions[0].id
    })
  }, [open, modelOptions, optionIds])

  const notifyError = (message: string) => {
    if (onRequestError) onRequestError(message)
  }

  const handleConfirm = async () => {
    const modelToSave = selectValue || selectedId
    if (!chatId || !modelToSave || submitting) return
    setSubmitting(true)
    try {
      const res = await fetch(`${apiBase}chat/sessions/${encodeURIComponent(chatId)}/model`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ model_name: modelToSave }),
      })
      if (!res.ok) {
        const msg = await getChatErrorToastMessage(res, 'Could not update the chat model.')
        notifyError(msg)
        return
      }
      const data = (await res.json()) as { model_name?: string }
      const locked = typeof data.model_name === 'string' ? data.model_name : modelToSave
      onRelocked(chatId, locked)
      onOpenChange(false)
    } catch {
      notifyError('Could not update the chat model. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  const empty = modelOptions.length === 0

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-md border shadow-lg"
        style={{
          background: 'var(--bg-modal, var(--background))',
          borderColor: 'var(--border-md)',
          color: 'var(--text-primary)',
        }}
      >
        <DialogHeader>
          <DialogTitle style={{ color: 'var(--text-primary)' }}>{SESSION_MODEL_RELOCK_TITLE}</DialogTitle>
          <DialogDescription style={{ color: 'var(--text-muted)' }}>
            {SESSION_MODEL_RELOCK_DIALOG_BODY}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-2 py-2">
          <label className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
            Lock this chat to
          </label>
          {empty ? (
            <p className="text-sm text-muted-foreground">
              No models are available right now. Ask an administrator to enable at least one chat model.
            </p>
          ) : (
            <Select value={selectValue} onValueChange={setSelectedId}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Choose a model" />
              </SelectTrigger>
              <SelectContent>
                {modelOptions.map(m => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => void handleConfirm()}
            disabled={submitting || empty || !chatId || !selectValue}
          >
            {submitting ? 'Saving…' : 'Lock model & close'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
